import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import { SplitText } from "gsap/SplitText"
import { module as BaseModule } from "modujs"

gsap.registerPlugin(SplitText, ScrollTrigger)

export default class extends BaseModule {
  init() {
    const { effect } = this.el.dataset
    this[effect]()
  }

  getEffectTiming(defaults = {}) {
    return {
      delay: this.el.dataset.effectDelay
        ? parseFloat(this.el.dataset.effectDelay)
        : defaults.delay ?? 0.3,
      duration: this.el.dataset.effectDuration
        ? parseFloat(this.el.dataset.effectDuration)
        : defaults.duration ?? 1,
    }
  }

  getUnderlineTarget(root = this.el) {
    if (!root.classList.contains("animate-underline")) return null

    return root.classList.contains("underline")
      ? root
      : root.querySelector(".underline")
  }

  separator() {
    ScrollTrigger.create({
      trigger: this.el,
      start: "top bottom",
      end: "bottom top",
      once: true,
      onEnter: () => {
        gsap.fromTo(
          this.el,
          {
            opacity: 0,
            width: 0,
          },
          {
            opacity: 1,
            duration: 1,
            width: "100%",
            ease: "power2.out",
          }
        )
      },
    })
  }

  fade() {
    const { delay, duration } = this.getEffectTiming()

    gsap.fromTo(
      this.el,
      {
        opacity: 0,
      },
      {
        opacity: 1,
        delay,
        duration,
      }
    )

    this.underline({ delay, duration: 0.6 })
  }

  delayedFade() {
    ScrollTrigger.create({
      trigger: this.el,
      start: "top bottom",
      end: "bottom top",
      once: true,
      onEnter: () => {
        this.fade()
      },
    })
  }

  underline(options = {}) {
    this.animateUnderline(this.el, options)
  }

  animateUnderline(root, options = {}) {
    const target = this.getUnderlineTarget(root)
    if (!target) return

    const delay = options.delay ?? this.getEffectTiming({ delay: 0.3 }).delay
    const duration =
      options.duration ?? this.getEffectTiming({ duration: 0.6 }).duration

    root.classList.add("is-underline-pending")

    gsap.fromTo(
      target,
      {
        "--underline-scale": 0,
      },
      {
        "--underline-scale": 1,
        delay,
        duration,
        ease: "power2.out",
        onStart: () => {
          root.classList.remove("is-underline-pending")
          root.classList.add("is-underline-in")
        },
        onComplete: () => {
          gsap.set(target, { clearProps: "--underline-scale" })
        },
      }
    )
  }

  delayedUnderline() {
    this.el.classList.add("is-underline-pending")

    ScrollTrigger.create({
      trigger: this.el,
      start: "top bottom",
      end: "bottom top",
      once: true,
      onEnter: () => {
        this.underline()
      },
    })
  }

  staggerSlide() {
    const items = gsap.utils.toArray(this.el.children)
    if (!items.length) return

    const { delay, duration } = this.getEffectTiming({
      delay: 0.3,
      duration: 0.8,
    })
    const stagger = this.el.dataset.effectStagger
      ? parseFloat(this.el.dataset.effectStagger)
      : 0.12
    const offset = this.el.dataset.effectOffset
      ? parseFloat(this.el.dataset.effectOffset)
      : 40

    items.forEach((item) => {
      const link = item.matches(".animate-underline")
        ? item
        : item.querySelector(".animate-underline")
      if (link) link.classList.add("is-underline-pending")
    })

    gsap.fromTo(
      items,
      {
        opacity: 0,
        x: offset,
      },
      {
        opacity: 1,
        x: 0,
        delay,
        duration,
        stagger,
        ease: "power2.out",
        onStart: () => {
          this.el.classList.add("is-stagger-in")
        },
        onComplete: () => {
          gsap.set(items, { clearProps: "transform,opacity" })
        },
      }
    )

    items.forEach((item, index) => {
      const link = item.matches(".animate-underline")
        ? item
        : item.querySelector(".animate-underline")
      if (!link) return

      this.animateUnderline(link, {
        delay: delay + index * stagger + duration * 0.25,
        duration: 0.6,
      })
    })
  }

  delayedSplit() {
    ScrollTrigger.create({
      trigger: this.el,
      start: "top bottom",
      end: "bottom top",
      once: true,
      onEnter: () => {
        this.split()
      },
    })
  }

  split() {
    const { delay } = this.getEffectTiming({ delay: 0 })

    document.fonts.ready.then(() => {
      let split
      SplitText.create(this.el, {
        type: "words,lines",
        linesClass: "line",
        autoSplit: true,
        mask: "lines",
        onSplit: (self) => {
          // Hide lines before revealing the parent — avoids a flash of
          // unmasked text during page transitions (e.g. homepage "Portfolio").
          gsap.set(self.lines, { yPercent: 100, opacity: 0 })
          gsap.set(this.el, { opacity: 1 })

          split = gsap.to(self.lines, {
            duration: 1.5,
            yPercent: 0,
            opacity: 1,
            stagger: 0.2,
            delay,
            ease: "expo.out",
          })
          return split
        },
      })
    })
  }
}
